import React from 'react';
import {
  Alert,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Select,
  Snackbar,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  DarkModeOutlined as DarkIcon,
  LightModeOutlined as LightIcon,
  SettingsBrightnessOutlined as SystemIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import useColorSchemePreference from '../hooks/useColorSchemePreference';

const OPTIONS = [
  { value: 'light', icon: LightIcon },
  { value: 'dark', icon: DarkIcon },
  { value: 'system', icon: SystemIcon },
];

/**
 * Escolha do esquema de cor (claro, escuro ou igual ao sistema). A troca vale
 * na hora neste dispositivo (localStorage `mui-mode` do MUI, para /mobile e
 * /dashboard) e é salva na preferência do colaborador
 * (useColorSchemePreference), aplicada de volta no próximo login. Sem escolha
 * vale o `defaultMode` do AppThemeProvider.
 *
 * `variant="select"` para linhas de formulário (perfil); `variant="toggle"`
 * para espaços compactos como o menu do avatar do dashboard.
 */
const ColorSchemeSelector = ({ variant = 'select' }) => {
  const { t } = useTranslation();
  const { mode, setMode, saveError, resetSaveError } = useColorSchemePreference();

  // `mode` só existe depois da montagem do provider.
  if (!mode) return null;

  const control = variant === 'toggle' ? (
    <ToggleButtonGroup
      exclusive
      size="small"
      value={mode}
      // Clicar na opção já selecionada devolve null: mantém a atual.
      onChange={(event, value) => value && setMode(value)}
      aria-label={t('colorScheme.label')}
    >
      {OPTIONS.map((option) => (
        <Tooltip key={option.value} title={t(`colorScheme.${option.value}`)}>
          <ToggleButton value={option.value} aria-label={t(`colorScheme.${option.value}`)}>
            <option.icon fontSize="small" />
          </ToggleButton>
        </Tooltip>
      ))}
    </ToggleButtonGroup>
  ) : (
    <Select
      value={mode}
      onChange={(event) => setMode(event.target.value)}
      size="small"
      inputProps={{ 'aria-label': t('colorScheme.label') }}
      renderValue={(value) => {
        const option = OPTIONS.find((item) => item.value === value);
        return (
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <option.icon fontSize="small" />
            <Typography variant="body2">{t(`colorScheme.${value}`)}</Typography>
          </Stack>
        );
      }}
    >
      {OPTIONS.map((option) => (
        <MenuItem key={option.value} value={option.value}>
          <ListItemIcon>
            <option.icon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t(`colorScheme.${option.value}`)}</ListItemText>
        </MenuItem>
      ))}
    </Select>
  );

  return (
    <>
      {control}
      {/* Só a gravação na conta falhou: o tema já foi trocado neste dispositivo. */}
      <Snackbar open={saveError} autoHideDuration={4000} onClose={resetSaveError}>
        <Alert severity="warning" onClose={resetSaveError}>
          {t('colorScheme.saveError')}
        </Alert>
      </Snackbar>
    </>
  );
};

export default ColorSchemeSelector;
